/**
 * Tall grass and rice: clumps in the lush meadows (waist-high) and the paddies (knee-high) that
 * sway in the wind, part round anyone wading through and swing back after. Each clump is a depth-sorted prop, so blades in front of a
 * fighter hide their legs. Purely visual: nothing here touches the simulation.
 */
import { hash2, mulberry32, Tile, tileAt, toScreen, type GameMap, type IsoOrigin } from '@nine/shared';
import { canvas, coarsen, ctx2d, finishArt } from './pixel';
import { PropGrid } from './propGrid';
import { tallGrassAt, type Prop, type ViewMapping } from './terrain';

/** Furthest a blade tip bends (pixels) either way; one frame per pixel of bend. */
const BEND = 6;
const FRAMES = BEND * 2 + 1;
/** How near (tiles) a passer-by has to come to part a clump. */
const REACH = 0.8;
const PUSH = 5.5;
/** The spring a clump swings back on: stiffness and damping (a little bouncy). */
const STIFF = 55;
const DAMP = 6.5;
/** Tall grass is drawn in blocks this many pixels across, coarser than the world's grid. */
const GRAIN = 2;
const SW = 28;
const SH = 26;
/** Clump variants: tall grass, then rice. */
const KINDS = 6;
const RICE_KINDS = 3;

type RGB = readonly [number, number, number];

/** Blade colours root to tip, and the seed heads on the tallest. */
const GREEN: readonly RGB[] = [
  [40, 72, 44],
  [62, 104, 52],
  [94, 136, 62],
  [138, 168, 84],
];
const STRAW: readonly RGB[] = [
  [74, 82, 46],
  [112, 118, 60],
  [150, 150, 78],
  [190, 182, 112],
];
const SEED: RGB = [196, 170, 116];
const RICE: readonly RGB[] = [
  [58, 102, 50],
  [84, 138, 58],
  [116, 168, 70],
  [160, 200, 98],
];

const css = ([r, g, b]: RGB, tint?: readonly [number, number, number]): string =>
  `rgb(${Math.min(255, Math.round(r * (tint?.[0] ?? 1)))},${Math.min(255, Math.round(g * (tint?.[1] ?? 1)))},${Math.min(255, Math.round(b * (tint?.[2] ?? 1)))})`;

/** One clump drawn at every bend from -BEND to +BEND, its roots at (SW/2, SH-2). */
function clumpFrames(
  kind: number,
  rice: boolean,
  tint?: readonly [number, number, number],
): HTMLCanvasElement[] {
  const r = mulberry32(kind * 97 + (rice ? 51 : 13));
  const blades = Array.from({ length: (rice ? 7 : 9) + Math.floor(r() * 4) }, () => ({
    x: Math.round((r() - 0.5) * (rice ? 7 : 12)),
    h: rice ? 7 + Math.floor(r() * 6) : 12 + Math.floor(r() * 10),
    lean: (r() - 0.5) * (rice ? 5 : 4),
    ramp: rice ? RICE : r() < (kind % 3 === 0 ? 0.45 : 0.15) ? STRAW : GREEN,
  }));
  // Taller blades at the back, so the short ones in front are drawn over them.
  blades.sort((a, b) => b.h - a.h);
  const tallest = blades[0]!.h;
  return Array.from({ length: FRAMES }, (_, f) => {
    // Rice stands stiffer than the meadow grass.
    const bend = (f - BEND) * (rice ? 0.6 : 1);
    const c = canvas(SW, SH);
    const g = ctx2d(c);
    const root = SH - 2;
    // A shadow on the meadow; a dark ring where the stems stand in the paddy's water.
    g.fillStyle = css(rice ? [52, 84, 82] : [28, 44, 30], tint);
    g.fillRect(SW / 2 - (rice ? 4 : 7), root, rice ? 8 : 14, 1);
    for (const b of blades) {
      // Stiff at the root, bending more towards the tip; a taller blade bends further.
      const reach = b.lean + bend * (b.h / tallest);
      let tip = [0, 0];
      for (let s = 0; s <= b.h; s++) {
        const t = s / b.h;
        const x = SW / 2 + b.x + Math.round(reach * t * t);
        const y = root - s + Math.round(Math.abs(bend) * 0.25 * t * t * (b.h / tallest)); // a bent blade droops
        g.fillStyle = css(b.ramp[t < 0.25 ? 0 : t < 0.55 ? 1 : t < 0.85 ? 2 : 3]!, tint);
        g.fillRect(x, y, s < 3 ? 2 : 1, 1);
        tip = [x, y];
      }
      if (!rice && b.h > tallest - 2) {
        g.fillStyle = css(SEED, tint);
        g.fillRect(tip[0]!, tip[1]! - 2, 1, 3);
      }
    }
    return c;
  });
}

interface Clump {
  /** Roots, in view coordinates. */
  gx: number;
  gy: number;
  kind: number;
  phase: number;
  /** Tip bend (pixels) and how fast it is changing. */
  bend: number;
  vel: number;
  /** Push from passers-by this frame. */
  push: number;
  prop: Prop;
}

/** Someone who can wade through the grass, in view coordinates. */
export interface Wader {
  id: number;
  x: number;
  y: number;
}

export class TallGrass {
  /** One prop per clump, re-imaged every frame as it bends. */
  readonly props: Prop[] = [];
  private readonly clumps: Clump[] = [];
  /** Clump indices per tile, to find those near a passer-by. */
  private readonly byTile = new Map<number, number[]>();
  private readonly frames: HTMLCanvasElement[][];
  private readonly last = new Map<number, [number, number]>();
  private cells?: PropGrid;
  /** The clumps' props by screen cell (prop `i` is clump `i`), at the clump size `visible` uses. */
  get grid(): PropGrid {
    return (this.cells ??= new PropGrid(
      this.clumps.map((c) => c.prop),
      () => [SW, SH],
    ));
  }

  constructor(
    private readonly m: GameMap,
    o: IsoOrigin,
    view: ViewMapping,
  ) {
    this.frames = [
      ...Array.from({ length: KINDS }, (_, k) =>
        clumpFrames(k, false, m.tint).map((f) => finishArt(coarsen(f, GRAIN), true)),
      ),
      ...Array.from({ length: RICE_KINDS }, (_, k) =>
        clumpFrames(k, true, m.tint).map((f) => finishArt(coarsen(f, GRAIN), true)),
      ),
    ];
    const add = (i: number, j: number, gx: number, gy: number, kind: number, phase: number) => {
      const [sx, sy] = toScreen(gx, gy, o);
      const prop: Prop = {
        img: this.frames[kind]![BEND]!,
        x: Math.round(sx - SW / 2),
        y: Math.round(sy - SH + 2),
        d: gx + gy,
      };
      const key = j * m.w + i;
      if (!this.byTile.has(key)) this.byTile.set(key, []);
      this.byTile.get(key)!.push(this.clumps.length);
      this.clumps.push({ gx, gy, kind, phase, bend: 0, vel: 0, push: 0, prop });
      this.props.push(prop);
    };
    for (let j = 0; j < m.h; j++)
      for (let i = 0; i < m.w; i++) {
        const [ui, uj] = view.unturn(i + 0.5, j + 0.5);
        const [a, b] = [Math.floor(ui), Math.floor(uj)];
        if (tileAt(m, i, j) === Tile.Paddy) {
          // Rice planted in four rows across the plot (the rows painted on the ground beneath).
          for (let q = 0; q < 4; q++) {
            const h = hash2(a * 13 + q, b * 29 + q * 5);
            add(
              i,
              j,
              i + q / 4 + 0.06,
              j + 0.2 + h * 0.6,
              KINDS + (Math.floor(h * 1000) % RICE_KINDS),
              h * 40,
            );
          }
          continue;
        }
        const thick = tallGrassAt(m, view, i, j);
        if (!thick) continue;
        // Clumps on a jittered 3×3 grid, thinning out towards the patch's edge.
        for (let q = 0; q < 9; q++) {
          const h = hash2(a * 31 + q * 7, b * 17 + q * 3);
          if (h > 0.25 + thick * 0.75) continue;
          const gx = i + ((q % 3) + 0.2 + hash2(a + q, b * 5) * 0.6) / 3;
          const gy = j + (Math.floor(q / 3) + 0.2 + hash2(a * 3, b + q * 11) * 0.6) / 3;
          add(i, j, gx, gy, Math.floor(hash2(a * 7 + q, b * 13) * KINDS), h * 40);
        }
      }
  }

  /** Steps the sway: wind (when `windy`), passers-by parting the blades, and the spring back. */
  update(
    dt: number,
    time: number,
    waders: readonly Wader[],
    windy: boolean,
    bounds?: { x0: number; x1: number; y0: number; y1: number },
  ): void {
    if (!this.clumps.length) return;
    dt = Math.min(dt, 1 / 20);
    const seen = new Set<number>();
    for (const w of waders) {
      seen.add(w.id);
      const was = this.last.get(w.id);
      this.last.set(w.id, [w.x, w.y]);
      const speed = was && dt > 0 ? Math.hypot(w.x - was[0], w.y - was[1]) / dt : 0;
      for (let b = Math.floor(w.y - REACH); b <= Math.floor(w.y + REACH); b++)
        for (let a = Math.floor(w.x - REACH); a <= Math.floor(w.x + REACH); a++) {
          const list = this.byTile.get(b * this.m.w + a);
          if (!list) continue;
          for (const n of list) {
            const c = this.clumps[n]!;
            if (bounds && !this.visible(c, bounds)) continue;
            const dx = c.gx - w.x;
            const dy = c.gy - w.y;
            const d = Math.hypot(dx, dy);
            if (d >= REACH) continue;
            // Blades lean away on screen: right of the wader bends right, left bends left.
            const side = dx - dy >= 0 ? 1 : -1;
            const k = 1 - d / REACH;
            c.push += side * k * PUSH;
            // Brushing past at speed gives them a flick that rings on after.
            c.vel += side * k * Math.min(speed, 6) * 9 * dt;
          }
        }
    }
    for (const id of this.last.keys()) if (!seen.has(id)) this.last.delete(id);
    // Off-screen springs pause (only clumps in view are pushed, so none build up while hidden).
    const near = bounds ? this.grid.query(bounds.x0, bounds.x1, bounds.y0, bounds.y1) : null;
    const count = near ? near.length : this.clumps.length;
    for (let n = 0; n < count; n++) {
      const c = this.clumps[near ? near[n]! : n]!;
      // Still air and nobody near: a clump that has settled stays as it is (most of them, most frames).
      if (!windy && c.push === 0 && c.vel === 0 && c.bend === 0) continue;
      const wind = windy
        ? Math.sin(time * 1.6 + c.phase + c.gx * 0.45 - c.gy * 0.3) * 1.1 +
          Math.max(0, Math.sin(time * 0.45 + (c.gx + c.gy) * 0.12)) * 1.6
        : 0;
      const want = Math.max(-BEND, Math.min(BEND, wind + c.push));
      c.vel += (STIFF * (want - c.bend) - DAMP * c.vel) * dt;
      c.bend = Math.max(-BEND, Math.min(BEND, c.bend + c.vel * dt));
      // Settled: snap to rest so the clump can be skipped until something stirs it again.
      if (want === 0 && Math.abs(c.bend) < 0.01 && Math.abs(c.vel) < 0.01) c.bend = c.vel = 0;
      c.push = 0;
      c.prop.img = this.frames[c.kind]![Math.round(c.bend) + BEND]!;
    }
  }

  private visible(c: Clump, bounds: { x0: number; x1: number; y0: number; y1: number }): boolean {
    return (
      c.prop.x + SW >= bounds.x0 &&
      c.prop.x <= bounds.x1 &&
      c.prop.y + SH >= bounds.y0 &&
      c.prop.y <= bounds.y1
    );
  }
}
